import Player from '../../common/Player';
import {useParams, useLocation} from 'react-router-dom';
import {useState, useEffect, useRef} from 'react';
import {getSerieByUrl, updateSerieEpisodeTime, markEpisodeWatched, unmarkEpisodeWatched} from '../../api';
import useUserStore from '../../hooks/useUserStore';
import ResponsiveContainer from '../../common/ResponsiveContainer';
import PlayerHeader from './PlayerHeader';
import EpisodeList, {MobileEpisodeScroll} from './EpisodeList';
import PlayerControls from './PlayerControls';

const Episode = () => {
    const {episode, serie} = useParams();
    const [serieData, setSerieData] = useState();
    const [error, setError] = useState(null);
    const [episodeNum, setEpisodeNum] = useState(Number(episode));
    const [episodeTime, setEpisodeTime] = useState(0);
    const [options, setOptions] = useState({
        controls: true,
        autoplay: true,
        sources: null,

        poster: '',
        updated: false,
    });
    const resumed = false;
    const playerContainerRef = useRef(null);
    const [playerHeight, setPlayerHeight] = useState(null);

    const user = useUserStore((state) => state.user);
    const location = useLocation();

    const currentEpisode = serieData?.capitulos?.find((c) => c.numero === episodeNum);
    const isWatched = !!currentEpisode?.completedAt;

    const updateEpisodeInState = (num, updates) => {
        setSerieData((prev) => ({
            ...prev,
            capitulos: prev.capitulos.map((c) => (c.numero === num ? {...c, ...updates} : c)),
        }));
    };

    const handleMarkWatched = () => {
        markEpisodeWatched(serie, episodeNum);
        updateEpisodeInState(episodeNum, {completedAt: new Date().toISOString()});
    };

    const handleUnmarkWatched = () => {
        unmarkEpisodeWatched(serie, episodeNum);
        updateEpisodeInState(episodeNum, {completedAt: undefined, time: 0});
    };

    const setEpisodeNumber = (number) => {
        const cap = serieData.capitulos.find((c) => Number(c.numero) === number);
        if (!cap) return;

        const poster = cap.thumbnail || cap.url.replace('turkish4love.com/videos/', 'turkish4love.com/thumbnails/').replace('.mp4', '.jpeg');
        setOptions((prev) => ({...prev, sources: {src: cap.url}, poster, updated: true}));
        setEpisodeNum(Number(cap.numero));

        window.scrollTo(0, 0);
        window.history.pushState({}, '', `/series/${serie}/${cap.numero}`);
    };

    useEffect(() => {
        getSerieByUrl(serie)
            .then((res) => {
                const cap = res.data.capitulos.find((c) => c.numero === Number(episodeNum));
                setEpisodeTime(cap?.time || 0);
                setSerieData(res.data);
            })
            .catch((err) => {
                setError(err?.response?.status === 403 ? 'forbidden' : 'error');
            });
    }, []);

    useEffect(() => {
        if (serieData) {
            setEpisodeNumber(episodeNum);
        }
    }, [serieData]);

    useEffect(() => {
        const cap = serieData?.capitulos?.find((c) => c.numero === Number(episode));
        if (cap?.url && cap.url !== options?.sources?.src) {
            const poster = cap.thumbnail || cap.url.replace('turkish4love.com/videos/', 'turkish4love.com/thumbnails/').replace('.mp4', '.jpeg');
            setOptions((prev) => ({...prev, sources: [{src: cap.url}], poster}));
        }
    }, [location]);

    useEffect(() => {
        if (!playerContainerRef.current || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setPlayerHeight(entry.contentRect.height);
            }
        });
        observer.observe(playerContainerRef.current);
        return () => observer.disconnect();
    }, []);

    const onPlayerReady = (player) => {
        player = player.current;
        if (!resumed && episodeTime) {
            player.currentTime(episodeTime);
        }

        let saveInterval = null;
        let markedWatched = false;

        const saveTime = () => {
            const time = player.currentTime();
            if (time > 5) updateSerieEpisodeTime(serie, time, episodeNum, player.duration() || undefined);
        };

        player.on('play', () => {
            saveInterval = setInterval(saveTime, 30000);
        });

        player.on('pause', () => {
            clearInterval(saveInterval);
            saveTime();
        });

        player.on('ended', () => {
            clearInterval(saveInterval);
            const dur = player.duration();
            updateSerieEpisodeTime(serie, dur, episodeNum, dur || undefined);
        });

        player.on('timeupdate', () => {
            const duration = player.duration();
            const remaining = duration - player.currentTime();
            if (!markedWatched && duration > 0 && remaining <= 180) {
                markedWatched = true;
                handleMarkWatched();
            }
        });
    };

    if (error) {
        return (
            <ResponsiveContainer className='pt-5 pb-4'>
                <div className='flex flex-col items-center justify-center min-h-[60vh] text-center gap-4'>
                    <p className='text-5xl'>🔒</p>
                    <h2 className='font-montserrat font-bold text-xl text-gray-800'>
                        {error === 'forbidden' ? 'No tienes acceso a este contenido' : 'Error al cargar el contenido'}
                    </h2>
                    <p className='font-montserrat text-gray-500 text-sm'>
                        {error === 'forbidden'
                            ? 'Tu cuenta no tiene permisos para ver este episodio.'
                            : 'Ocurrió un error inesperado. Intenta de nuevo más tarde.'}
                    </p>
                </div>
            </ResponsiveContainer>
        );
    }

    return (
        <ResponsiveContainer className='pt-5 pb-4'>
            <PlayerHeader
                serieTitle={serieData?.titulo_original}
                serieUrl={serie}
                episodeNum={episodeNum}
                temporada={currentEpisode?.temporada}
            />

            <div className='flex gap-3 items-start'>
                <div className='flex-1 flex flex-col min-w-0'>
                    <div ref={playerContainerRef} className='flex flex-col'>
                        <div className='bg-black rounded-lg overflow-hidden shadow'>
                            {options.sources ? (
                                <Player options={options} onReady={onPlayerReady}/>
                            ) : (
                                <div className='w-full aspect-video bg-gray-200 animate-pulse'/>
                            )}
                        </div>

                        <PlayerControls
                            onPrev={() => setEpisodeNumber(episodeNum - 1)}
                            onNext={() => setEpisodeNumber(episodeNum + 1)}
                            watched={isWatched}
                            onMarkWatched={handleMarkWatched}
                            onUnmarkWatched={handleUnmarkWatched}
                        />
                    </div>

                    {/* Mobile episode scroll */}
                    <div className='md:hidden bg-white rounded-lg shadow border border-gray-100 mt-3'>
                        <div className='flex items-center justify-between px-4 py-3 border-b border-gray-100'>
                            <span className='font-montserrat font-semibold text-xs text-gray-500 uppercase tracking-wider'>Episodios</span>
                            {serieData?.capitulos?.length > 0 && (
                                <span className='text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full font-montserrat'>
                  {serieData.capitulos.length} eps
                </span>
                            )}
                        </div>
                        <MobileEpisodeScroll
                            capitulos={serieData?.capitulos}
                            activeEpisodeNum={episodeNum}
                            onSelectEpisode={setEpisodeNumber}
                            fallbackImage={serieData?.imagen_catalogo}
                        />
                    </div>
                </div>

                <EpisodeList
                    capitulos={serieData?.capitulos}
                    activeEpisodeNum={episodeNum}
                    onSelectEpisode={setEpisodeNumber}
                    maxHeight={playerHeight}
                    fallbackImage={serieData?.imagen_catalogo}
                />
            </div>
        </ResponsiveContainer>
    );
};

export default Episode;
